import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthentication } from '../Idp/useAuthentication';
import usePollingUpdater from '../utils/usePollingUpdater';
import { useCoreApi } from './api';

export interface ICredential {
  card_type: TokenExCardTypes;
  created_at: string;
  display: string;
  image: string;
  last_four: string;
  month: string;
  year: string;
  status: string;
  iframe: CreditCardConfig;
  stripe_payment_method: { token: string; status: string; uuid: string };
  givebacks_tokenized: boolean;
  token: string;
  type: string;
  updated_at: string;
  uuid: string;
  rewards_network?: boolean;
  rakuten?: boolean;
}
export type StripeCardTypes =
  | 'Visa'
  | 'MasterCard'
  | 'American Express'
  | 'Discover'
  | 'Diners Club';

export type TokenExCardTypes =
  | 'masterCard'
  | 'americanExpress'
  | 'visa'
  | 'discover'
  | 'diners'
  | 'jcb';

export interface ICredentialForm {
  token: string;
  hmac: string;
  metadata: {
    card_type: TokenExCardTypes;
    last_four: string;
    full_name?: string;
    month?: string;
    year?: string;
    zip?: string;
  };
}

export interface CreditCardConfig {
  authentication_key: string;
  app_id: string;
  timestamp: string;
  scheme: string;
}

export interface Card {
  cardType: TokenExCardTypes;
  cvvIncluded: boolean;
  firstSix: string;
  lastFour: string;
  referenceNumber: string;
  token: string;
  tokenHMAC: string;
}

/**  List all user payment methods */
export function useListUserCreditCards<T = ICredential[]>(select?: (data: ICredential[]) => T) {
  const { isAuthenticated } = useAuthentication();
  const CoreApi = useCoreApi();

  const getCreditCards = async (): Promise<ICredential[]> => {
    const { data } = await CoreApi.get(`/credit_cards`);
    return data.credentials;
  };

  return useQuery({
    queryKey: ['user', 'credit_cards'],
    queryFn: getCreditCards,
    enabled: isAuthenticated,
    select,
    placeholderData: [],
  });
}

export function useShowUserCreditCard(uuid: string) {
  const CoreApi = useCoreApi();

  const getCreditCard = async (): Promise<ICredential> => {
    const { data } = await CoreApi.get(`/credit_cards/${uuid}`);
    return data.credential;
  };

  return useQuery({
    queryKey: ['user', 'credit_cards', uuid],
    queryFn: getCreditCard,
    enabled: !!uuid,
  });
}

/**  List all user payment methods through TokenEx */
export function useListUserPaymentMethods<T = ICredential[]>(select?: (data: ICredential[]) => T) {
  const { isAuthenticated } = useAuthentication();
  const CoreApi = useCoreApi();

  const getPaymentMethods = async (): Promise<ICredential[]> => {
    const { data } = await CoreApi.get(`/payment_methods`);
    return data.credentials;
  };

  return useQuery({
    queryKey: ['user', 'payment_methods'],
    queryFn: getPaymentMethods,
    enabled: isAuthenticated,
    select,
    placeholderData: [],
  });
}

/**  Show a specific payment method */
export function useShowUserPaymentMethod(uuid: string) {
  const CoreApi = useCoreApi();

  const getPaymentMethod = async (): Promise<ICredential> => {
    const { data } = await CoreApi.get(`/payment_methods/${uuid}`);
    return data.credential;
  };

  return useQuery({
    queryKey: ['user', 'payment_methods', uuid],
    queryFn: getPaymentMethod,
    enabled: !!uuid,
  });
}

export const useCreateStripePaymentMethod = () => {
  const CoreApi = useCoreApi();

  const create = async (form: ICredentialForm): Promise<ICredential> => {
    const { data } = await CoreApi.post(`/stripe/payment_methods`, { credential: form });
    return data.credential;
  };

  return useMutation({
    mutationFn: create,
    mutationKey: ['user', 'payment_methods'],
  });
};

export const useTokenizeStripeCard = () => {
  const CoreApi = useCoreApi();
  const queryClient = useQueryClient();

  const create = async (uuid: string): Promise<ICredential> => {
    const { data } = await CoreApi.post(`/stripe/payment_methods/${uuid}/token_ex`, {});
    return data.credential;
  };

  return useMutation({
    mutationFn: create,
    onSuccess() {
      return queryClient.invalidateQueries({
        queryKey: ['user', 'payment_methods'],
      });
    },
  });
};

export const useExistingCardCredential = () => {
  const CoreApi = useCoreApi();

  const createStripePaymentMethod = async (uuid: string): Promise<ICredential> => {
    const { data } = await CoreApi.post(`/payment_methods/${uuid}/stripe`, {});
    return data.credential;
  };

  return useMutation({
    mutationFn: createStripePaymentMethod,
    mutationKey: ['user', 'payment_methods'],
  });
};

/** Fetch a stripe credential */
export function useShowStripeCredential(uuid?: string) {
  const CoreApi = useCoreApi();
  const fetchCredential = async (): Promise<ICredential> => {
    const { data } = await CoreApi.get(`/stripe/payment_methods/${uuid}`);
    return data.credential;
  };

  return useQuery({
    queryKey: ['core', 'credentials', uuid],
    queryFn: fetchCredential,
    enabled: !!uuid,
  });
}

/**  Check if a user has either of the card types */

export function useSelectHasEitherCardTypes() {
  return useListUserPaymentMethods((credentials: ICredential[]) =>
    credentials.some((credential) => credential.rewards_network || credential.rakuten)
  );
}

/**  Create a normal Payment method */
export function useCreateUserPaymentMethod() {
  const CoreApi = useCoreApi();
  const queryClient = useQueryClient();
  const { data: hasPartnerCard } = useSelectHasEitherCardTypes();
  const { pollQuery } = usePollingUpdater({ useQuery: useSelectHasEitherCardTypes });
  const createPaymentMethod = async (credential: ICredentialForm) => {
    const { data } = await CoreApi.post<ICredential>(`/payment_methods`, {
      credential: credential,
    });
    return data;
  };

  return useMutation({
    mutationFn: createPaymentMethod,
    onSuccess() {
      // We can use return here so that our mutation stays in a loading state while the invalidation happens.
      // Not returning a mutation means that it's not a promise and thus the invalidate is fired and forgotten
      // We can either invalidate or just insert card into the stack, probably would be better to just add it in.
      return queryClient.invalidateQueries({
        queryKey: ['user', 'payment_methods'],
      });
    },
    onSettled() {
      !hasPartnerCard && pollQuery();
    },
  });
}

const useGetPaymentMethod = () => useListUserPaymentMethods();

export function useDeleteUserPaymentMethod() {
  const CoreApi = useCoreApi();
  const queryClient = useQueryClient();
  const { data: hasPartnerCard } = useSelectHasEitherCardTypes();
  const { pollQuery } = usePollingUpdater({ useQuery: useGetPaymentMethod });

  const deletePaymentMethod = async (uuid: string) => {
    const { data } = await CoreApi.delete(`/payment_methods/${uuid}`);
    return data;
  };

  return useMutation({
    mutationFn: deletePaymentMethod,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ['user', 'payment_methods'],
      });
    },
    onSettled() {
      !hasPartnerCard && pollQuery();
    },
  });
}

export function useDeleteUserCreditCard() {
  const CoreApi = useCoreApi();
  const queryClient = useQueryClient();

  const deleteCreditCard = async (uuid: string) => {
    const { data } = await CoreApi.delete(`/credit_cards/${uuid}`);
    return data;
  };

  return useMutation({
    mutationFn: deleteCreditCard,
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: ['user', 'credit_cards'],
      });
    },
  });
}
